import clsx from 'clsx'
import { addDays, format, isSameDay } from 'date-fns'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { useChoiceState } from '@context/ChoiceContext'
import { useConfig } from '@context/ConfigurationContext'
import { useDateFnsLocale } from '@hooks/useDateFnsLocale'
import { useGetTreatmentName } from '@hooks/useGetTreatmentName'
import { useSelectTimeslot } from '@hooks/useSelectTimeslot'
import useStyles from './styles'
import { completedInsurance, completedPatientType } from 'src/services/analytics'
import { useHistory } from 'react-router-dom'
import routes from 'src/config/routes'
import { useNearestAvailabilities } from '@hooks/useNearestAvailabilities'
import { DATE_FORMATS } from 'src/config/constants'
import { Availability } from '@customTypes/Availabilities'

const LAST_DISPLAYED_SLOT_INDEX = 5

interface Props {
  afterTimeslotSelect: () => void
}

export const NearestTimeslots = ({ afterTimeslotSelect }: Props) => {
  const classes = useStyles()
  const { t } = useTranslation()
  const { clinicId, isInternalClinic } = useConfig()
  const { selectedBookableDateAndTime } = useChoiceState()
  const treatmentName = useGetTreatmentName()
  const selectTimeslot = useSelectTimeslot()
  const history = useHistory()
  const { data: nearestAvailabilities } = useNearestAvailabilities()

  const [nearestDayAvailabilities] = nearestAvailabilities || []
  const { date, availabilities } = nearestDayAvailabilities || {}
  const dateWithNearestAvailabilities = date ? new Date(date) : 0
  const todayIsDayWithNearestAvailabilities = isSameDay(new Date(), dateWithNearestAvailabilities)

  const tomorrowIsDayWithNearestAvailabilities = isSameDay(addDays(new Date(), 1), dateWithNearestAvailabilities)
  const nearestDayLabel = (() => {
    if (todayIsDayWithNearestAvailabilities) return `${t('today')}, `
    if (tomorrowIsDayWithNearestAvailabilities) return `${t('tomorrow')}, `
    return ''
  })()

  const locale = useDateFnsLocale()
  const formattedDate = dateWithNearestAvailabilities
    ? format(dateWithNearestAvailabilities, DATE_FORMATS.DAY_MONTH, { locale })
    : null

  const slotsToDisplay = (availabilities || []).slice(0, LAST_DISPLAYED_SLOT_INDEX)

  const handleMoreClicked = () => {
    if (!isInternalClinic) completedPatientType()
    completedInsurance()
    history.push(routes.appointment)
  }

  const handleSelectTimeslot = (availability: Availability, clinicId?: string) => {
    selectTimeslot(availability, clinicId)
    afterTimeslotSelect()
  }

  return (
    <>
      {slotsToDisplay.length > 0 && (
        <div className={classes.nextAvailableSlotsContainer}>
          <div>
            <span className={classes.nextAvailableSlotsText}>{t('next_available_appointments')}</span>
            <span className={classes.boldedText}>{treatmentName}</span>
          </div>
          <div className={classes.divider} />
          <div>
            <span className={classes.boldedText}>
              {nearestDayLabel}
              {formattedDate}
            </span>
          </div>
          <div className={classes.slotsContainer}>
            {slotsToDisplay.map((slot, index) => (
              <div
                key={slot.originalStartTimestamp}
                className={clsx(classes.slot, {
                  [classes.selected]:
                    selectedBookableDateAndTime.originalStartTimestamp === slot.originalStartTimestamp,
                  [classes.lastSlot]:
                    slotsToDisplay.length === LAST_DISPLAYED_SLOT_INDEX && index === slotsToDisplay.length - 1,
                })}
                onClick={() => handleSelectTimeslot(slot, clinicId)}
              >
                {slot.start.substring(0, LAST_DISPLAYED_SLOT_INDEX)}
              </div>
            ))}
          </div>
          <div className={clsx(classes.slot, classes.moreSlot)} onClick={handleMoreClicked}>
            {t('more')}
          </div>
        </div>
      )}
    </>
  )
}
